import React, { useState, useRef } from 'react';
import { Card, Form, Button, Alert, Spinner, Table, Badge, Modal, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faUsers, 
  faFileExcel, 
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faEye,
  faPaperPlane,
  faTrash,
  faDownload
} from '@fortawesome/free-solid-svg-icons';

interface AgentData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  licenseNumber: string;
  specialties: string;
  yearsExperience: number;
  status: 'valid' | 'invalid' | 'duplicate';
  errors: string[];
}

interface ImportStats {
  total: number;
  valid: number;
  invalid: number;
  duplicates: number;
}

const BrokerAgentImport: React.FC = () => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [agentData, setAgentData] = useState<AgentData[]>([]);
  const [importStats, setImportStats] = useState<ImportStats>({ total: 0, valid: 0, invalid: 0, duplicates: 0 });
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setErrors(['Please upload a CSV file']);
      return;
    }

    setUploadedFile(file);
    setUploading(true);
    setErrors([]);
    setSuccess('');

    try {
      const text = await file.text();
      const parsedData = parseCSVData(text);
      setAgentData(parsedData);
      setShowPreview(true);
    } catch (error) {
      setErrors(['Error parsing CSV file. Please check the format.']);
    } finally {
      setUploading(false);
    }
  };

  const parseCSVData = (csvText: string): AgentData[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) throw new Error('CSV must have header and data rows');

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const agents: AgentData[] = [];
    let valid = 0, invalid = 0, duplicates = 0;

    const emailSet = new Set<string>();

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      
      const agent: AgentData = {
        id: `agent_${i}`,
        firstName: getValue(values, headers, ['first_name', 'firstname', 'first name']),
        lastName: getValue(values, headers, ['last_name', 'lastname', 'last name']),
        email: getValue(values, headers, ['email', 'email_address', 'email address']),
        phone: getValue(values, headers, ['phone', 'phone_number', 'mobile', 'cell']),
        licenseNumber: getValue(values, headers, ['license', 'license_number', 'license number', 'license_id']),
        specialties: getValue(values, headers, ['specialties', 'specialty', 'focus_areas', 'areas']),
        yearsExperience: parseInt(getValue(values, headers, ['experience', 'years_experience', 'years experience', 'years'])) || 0,
        status: 'valid',
        errors: []
      };

      // Validation
      if (!agent.firstName) agent.errors.push('Missing first name');
      if (!agent.lastName) agent.errors.push('Missing last name');
      if (!agent.email || !isValidEmail(agent.email)) agent.errors.push('Invalid email');
      if (!agent.phone) agent.errors.push('Missing phone number');

      // Check for duplicates
      if (emailSet.has(agent.email.toLowerCase())) {
        agent.errors.push('Duplicate email');
        duplicates++;
      } else {
        emailSet.add(agent.email.toLowerCase());
      }

      if (agent.errors.length > 0) {
        agent.status = agent.errors.includes('Duplicate email') ? 'duplicate' : 'invalid';
        invalid++;
      } else {
        valid++;
      }

      agents.push(agent);
    }

    setImportStats({ total: agents.length, valid, invalid, duplicates });
    return agents;
  };

  const getValue = (values: string[], headers: string[], possibleNames: string[]): string => {
    for (const name of possibleNames) {
      const index = headers.findIndex(h => h.includes(name));
      if (index !== -1 && values[index]) {
        return values[index];
      }
    }
    return '';
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendInvitations = async () => {
    setSending(true);
    setErrors([]);
    setSuccess('');

    try {
      const validAgents = agentData.filter(agent => agent.status === 'valid');
      
      const response = await fetch('http://localhost:8080/api/broker/invite-agents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          agents: validAgents,
          brokerageId: localStorage.getItem('brokerageId') || 'default_brokerage'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send invitations');
      }

      const result = await response.json();
      
      if (result.success) {
        setSuccess(`Successfully sent ${validAgents.length} invitations to join the platform!`);
        setAgentData([]);
        setUploadedFile(null);
        setShowPreview(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setErrors([result.error || 'Failed to send invitations']);
      }
    } catch (error) {
      setErrors(['Error sending invitations. Please try again.']);
    } finally {
      setSending(false);
    }
  };

  const clearData = () => {
    setAgentData([]);
    setUploadedFile(null);
    setShowPreview(false);
    setErrors([]);
    setSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'First Name,Last Name,Email,Phone,License Number,Specialties,Years Experience\nJohn,Doe,john@example.com,(555) 123-4567,12345,Residential Sales,5\nJane,Smith,jane@example.com,(555) 987-6543,67890,Commercial Leasing,8';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agent_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };


  return (
    <div className="broker-agent-import">
      {/* Header */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-gradient-primary text-white d-flex align-items-center">
          <FontAwesomeIcon icon={faUpload} className="me-3" size="lg" />
          <div>
            <h4 className="mb-1">CSV Agent Import</h4>
            <p className="mb-0 opacity-75">Upload a CSV file to bulk invite agents to join your brokerage on the platform</p>
          </div>
        </Card.Header>
      </Card>

      {/* Alerts */}
      {errors.length > 0 && (
        <Alert variant="danger" className="mb-4">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <strong>Error:</strong>
          <ul className="mb-0 mt-2">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          {success}
        </Alert>
      )}

      {/* Upload Section */}
      {!showPreview && (
        <Row>
          <Col md={8}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <h5 className="mb-3">
                  <FontAwesomeIcon icon={faUpload} className="me-2 text-primary" />
                  Upload Agent CSV File
                </h5>
                
                <Form.Group className="mb-4">
                  <Form.Label>Select CSV File</Form.Label>
                  <Form.Control
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Upload a CSV file containing agent information. Maximum file size: 10MB
                  </Form.Text>
                </Form.Group>

                {uploading && (
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" className="me-2" />
                    Processing CSV file...
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={4}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <h6 className="mb-3">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
                  CSV Format Requirements
                </h6>
                
                <div className="small text-muted mb-3">
                  <p><strong>Required columns:</strong></p>
                  <ul className="mb-2">
                    <li>First Name</li>
                    <li>Last Name</li>
                    <li>Email</li>
                    <li>Phone</li>
                  </ul>
                  
                  <p><strong>Optional columns:</strong></p>
                  <ul className="mb-3">
                    <li>License Number</li>
                    <li>Specialties</li>
                    <li>Years Experience</li>
                  </ul>
                </div>

                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={downloadTemplate}
                  className="w-100"
                >
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Download Template
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Preview Section */}
      {showPreview && (
        <div>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-light d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faEye} className="me-2 text-primary" />
                <h5 className="mb-0">Agent Preview</h5>
                <Badge bg="secondary" className="ms-2">{uploadedFile?.name}</Badge>
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={clearData}>
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Clear
                </Button>
                <Button 
                  variant="success" 
                  onClick={handleSendInvitations}
                  disabled={sending || importStats.valid === 0}
                >
                  {sending ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                      Send {importStats.valid} Invitations
                    </>
                  )}
                </Button>
              </div>
            </Card.Header>
          </Card>

          {/* Import Statistics */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="border-0 bg-primary text-white">
                <Card.Body className="text-center">
                  <h3 className="mb-1">{importStats.total}</h3>
                  <small>Total Agents</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-success text-white">
                <Card.Body className="text-center">
                  <h3 className="mb-1">{importStats.valid}</h3>
                  <small>Valid</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-warning text-white">
                <Card.Body className="text-center">
                  <h3 className="mb-1">{importStats.invalid}</h3>
                  <small>Invalid</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-secondary text-white">
                <Card.Body className="text-center">
                  <h3 className="mb-1">{importStats.duplicates}</h3>
                  <small>Duplicates</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Agent Data Table */}
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <Table striped hover className="mb-0">
                  <thead className="bg-light sticky-top">
                    <tr>
                      <th>Status</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>License</th>
                      <th>Experience</th>
                      <th>Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentData.map((agent) => (
                      <tr key={agent.id}>
                        <td>
                          <Badge 
                            bg={
                              agent.status === 'valid' ? 'success' : 
                              agent.status === 'duplicate' ? 'secondary' : 'danger'
                            }
                          >
                            {agent.status}
                          </Badge>
                        </td>
                        <td>{agent.firstName} {agent.lastName}</td>
                        <td>{agent.email}</td>
                        <td>{agent.phone}</td>
                        <td>{agent.licenseNumber || 'N/A'}</td>
                        <td>{agent.yearsExperience > 0 ? `${agent.yearsExperience} years` : 'N/A'}</td>
                        <td>
                          {agent.errors.length > 0 && (
                            <small className="text-danger">
                              {agent.errors.join(', ')}
                            </small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BrokerAgentImport;